import { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { useCageDate, shiftDate } from '../contexts/CageDateContext';

// ----------------------------------------------------------------
// CageDatePicker
// ----------------------------------------------------------------
// The business-date control shared by every Cage Balancing tab. Replaces the
// bare <input type="date"> each page used to render.
//
// What it adds over the native input:
//  * prev / next day arrows - stepping a day at a time is the common action,
//    and the native picker made you open a calendar to do it
//  * the weekday spelled out, so you can see at a glance which gaming day
//    you're on
//  * a Today button, and an amber "back day" cue whenever you are not on the
//    current gaming day, so a stale date is never invisible
//  * left / right arrow keys step the day while the control has focus
//
// The date lives in CageDateContext, which stores the ANCHOR day. Pass the same
// offsetDays this page passes to useCageDate, so the control shows and edits the
// date this tab is actually on. Free Play and Chip Liability trail the anchor by
// a day (CAGE_OFFSET.CLOSED_DAY); Balance Sheet and Drop Count sit on it.
//
// showEntryDate flips what the CONTROL displays without changing which day the
// page loads. Free Play and Chip Liability are filled in on the morning AFTER the
// gaming day they describe, and the cage thinks in terms of the morning they are
// sitting there doing the work - so those tabs put the entry date in the picker
// and name the gaming day beside it ("Revenue date Thu, Sep 17"). The data still
// comes from offsetDays; only the label moves.

const fmtLong = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
};

const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

function relLabel(businessDate, today) {
  const n = daysBetween(businessDate, today);
  if (n === 0) return null;
  if (n === 1) return 'yesterday';
  if (n === -1) return 'tomorrow';
  if (n > 1) return `${n} days back`;
  return `${Math.abs(n)} days ahead`;
}

export default function CageDatePicker({ right = null, className = '', offsetDays = 0, showEntryDate = false }) {
  const { businessDate, setBusinessDate, stepDays, goToday, today } = useCageDate(offsetDays);
  const wrapRef = useRef(null);

  // What the control shows and compares against. With showEntryDate this is the
  // anchor - the morning the count is taken - so 'Today' and the amber back-day
  // cue read against the day the person is actually working, not the gaming day.
  const shownDate = showEntryDate ? shiftDate(businessDate, -offsetDays) : businessDate;
  const shownToday = showEntryDate ? shiftDate(today, -offsetDays) : today;
  const isToday = shownDate === shownToday;
  const rel = relLabel(shownDate, shownToday);

  // Typing a date sets the anchor so every other tab moves with it.
  const onPick = (v) => setBusinessDate(showEntryDate ? shiftDate(v, offsetDays) : v);

  // Left/right arrows step the day, but never while typing in the date field.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); stepDays(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); stepDays(1); }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [stepDays]);

  const arrow =
    'flex items-center justify-center h-9 w-9 rounded-lg border border-gray-700 bg-gray-900 ' +
    'text-gray-300 hover:text-white hover:border-gray-500 focus:outline-none focus:border-amber-500';

  return (
    <div ref={wrapRef} tabIndex={-1} className={`flex flex-wrap items-center gap-2 ${className}`}>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => stepDays(-1)} className={arrow}
          title={`Previous day (${fmtLong(shiftDate(shownDate, -1))})`} aria-label="Previous day">
          <ChevronLeft size={18} />
        </button>

        <div className="relative flex items-center">
          <CalendarDays size={16} className="pointer-events-none absolute left-3 text-gray-500" />
          <input
            type="date"
            value={shownDate}
            onChange={(e) => onPick(e.target.value)}
            aria-label={showEntryDate ? 'Entry date' : 'Business date'}
            className="h-9 rounded-lg border border-gray-700 bg-gray-900 pl-9 pr-2 text-sm font-mono
                       text-white focus:outline-none focus:border-amber-500 [color-scheme:dark]"
          />
        </div>

        <button type="button" onClick={() => stepDays(1)} className={arrow}
          title={`Next day (${fmtLong(shiftDate(shownDate, 1))})`} aria-label="Next day">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {showEntryDate ? (
          <span className="text-sm text-gray-300">
            <span className="text-gray-500">Revenue date </span>
            {fmtLong(businessDate)}
          </span>
        ) : (
          <span className="text-sm text-gray-300">{fmtLong(businessDate)}</span>
        )}
        {rel && (
          <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5
                           text-[11px] font-medium text-amber-400">
            {rel}
          </span>
        )}
        {!isToday && (
          <button type="button" onClick={goToday}
            className="rounded-lg border border-gray-700 bg-gray-900 px-3 h-9 text-sm text-gray-300
                       hover:text-white hover:border-gray-500 focus:outline-none focus:border-amber-500"
            title={`Jump to ${showEntryDate ? 'today' : 'the current gaming day'} (${fmtLong(shownToday)})`}>
            Today
          </button>
        )}
      </div>

      {right}
    </div>
  );
}
